import { useTranslation } from '@rhino-project/core/i18n';
import { ComponentProps, ElementType, ReactNode, useState } from 'react';

import { Button, ButtonProps, Link, LinkProps } from '@heroui/react';
import { Icon } from '@iconify/react';

type IconButtonProps = {
  as?: ElementType;
  icon: string;
  color?: string;
  children?: ReactNode;
  isPending?: boolean;
  [key: string]: unknown;
};

export const IconButton = ({
  as: As,
  icon,
  color,
  children,
  isPending,
  ...props
}: IconButtonProps) => {
  if (As) {
    return (
      <As
        className={`button button--md button--${color || 'primary'}`}
        {...props}
      >
        <Icon className="s-8" icon={icon} />
        {children}
      </As>
    );
  }

  return (
    // HeroUI v3 Button has no `color` prop; the original passed it through and
    // HeroUI ignored it, so preserve that by spreading it as an extra attr.
    // isPending disables the button and shows the spinner while work is in
    // flight (guards against double-submits).
    <Button {...({ color } as object)} isPending={isPending} {...props}>
      <Icon className="s-8" icon={icon} />
      {children}
    </Button>
  );
};

type OmniIconButtonProps = ButtonProps & {
  icon: string;
  name?: string;
  endpoint: string;
};

export const OmniIconButton = ({
  icon,
  name,
  endpoint,
  ...props
}: OmniIconButtonProps) => {
  const { t } = useTranslation();
  const [isPending, setIsPending] = useState(false);

  return (
    <form method="post" action={endpoint} onSubmit={() => setIsPending(true)}>
      <Button
        variant="outline"
        className="w-full"
        type="submit"
        isPending={isPending}
        {...props}
      >
        <Icon style={{ height: '1.25rem', width: '1.25rem' }} icon={icon} />
        {t('rhino.frontend.messages.continue_with_provider', {
          provider: name || 'OAuth'
        })}
      </Button>
    </form>
  );
};

export const SubmitButton = (props: ButtonProps) => (
  <Button variant="primary" type="submit" className="w-full" {...props} />
);

// LinkButton forwards arbitrary props (including router props like `to`) onto
// the underlying Link, which is polymorphic at runtime; allow extra props.
export const LinkButton = (props: LinkProps & { [key: string]: unknown }) => (
  <Link
    className="button button--md button--primary"
    {...(props as LinkProps)}
  />
);

export const DismissButton = (props: ComponentProps<typeof Button>) => (
  <Button isIconOnly {...props}>
    <Icon icon="bi:x" />
  </Button>
);
